import { type ReactElement, useState } from "react";

import { Combobox } from "@similarweb/ui-components";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import { DROPDOWN_AUTO_OPEN_DELAY } from "app/pages/sales-intelligence/sub-modules/signals/constants/dropdown";

import FilterButtonLabel from "../shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "../shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import { StyledSignalFilterBlock } from "../shared/styles";

interface Props<T> {
    selectedValue: T | undefined;
    options: readonly T[];
    autoOpen?: boolean;
    onValueChange(value: T): void;
}

const PERIOD_DROPDOWN_WIDTH = "150px";

const getPeriodTranslatable = (period: string) => ({
    key: `si.signals.changing-traction.${period}.label`,
});

const SignalPeriodsFilter = <T extends string>({
    selectedValue,
    options,
    autoOpen = false,
    onValueChange,
}: Props<T>): ReactElement => {
    const [isOpen, setIsOpen] = useState(false);
    const hasValue = typeof selectedValue !== "undefined";

    useDelayedAction(DROPDOWN_AUTO_OPEN_DELAY, () => setIsOpen(true), autoOpen);

    const handleSelect = (period: T): void => {
        setIsOpen(false);
        onValueChange(period);
    };

    return (
        <StyledSignalFilterBlock>
            <Combobox
                data-automation="signal-period-dd"
                placement="bottom-start"
                width={PERIOD_DROPDOWN_WIDTH}
                isOpen={isOpen}
                onOpenChange={setIsOpen}
            >
                <Combobox.Trigger>
                    <SignalFilterDropdownButton
                        id="traffic-period"
                        isOpen={isOpen}
                        hasValue={hasValue}
                    >
                        {hasValue ? (
                            <I18n
                                data-automation="period-dd-button-text"
                                element={FilterButtonLabel}
                                translatable={getPeriodTranslatable(selectedValue)}
                            />
                        ) : null}
                    </SignalFilterDropdownButton>
                </Combobox.Trigger>

                <Combobox.Content selectionMode="single">
                    {options.map((option) => (
                        <Combobox.Option
                            key={option}
                            id={option}
                            textValue={getPeriodTranslatable(option).key}
                            isSelected={option === selectedValue}
                            onClick={() => handleSelect(option)}
                        >
                            <Combobox.OptionLabel>
                                <I18n
                                    data-automation="period-dd-item-text"
                                    translatable={getPeriodTranslatable(option)}
                                />
                            </Combobox.OptionLabel>
                        </Combobox.Option>
                    ))}
                </Combobox.Content>
            </Combobox>
        </StyledSignalFilterBlock>
    );
};

export default SignalPeriodsFilter;
